import { useState, useRef, useEffect } from "react";
import { ArrowRight, Youtube, LogOut } from "lucide-react";
import Wordmark from "./components/Wordmark";
import ResultCards from "./components/ResultCards";
import OnboardingQuiz from "./components/OnboardingQuiz";
import UpgradeModal from "./components/UpgradeModal";
import AuthModal from "./components/AuthModal";

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:8000";

function getStoredUser() {
  const token = localStorage.getItem("yousift_token");
  const user_id = localStorage.getItem("yousift_user_id");
  const email = localStorage.getItem("yousift_email");
  if (token && user_id && email) return { token, user_id, email };
  return null;
}

export default function App() {
  const [url, setUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [progress, setProgress] = useState(0);
  const [user, setUser] = useState(() => getStoredUser());
  const [showAuth, setShowAuth] = useState(false);
  const [showQuiz, setShowQuiz] = useState(false);
  const [upgradeReason, setUpgradeReason] = useState(null);
  const [pendingUrl, setPendingUrl] = useState(null); // URL waiting for auth
  const resultsRef = useRef(null);

  // On load, verify token and check quiz status
  useEffect(() => {
    if (!user) return;
    fetch(`${API_BASE}/api/auth/me`, {
      headers: { Authorization: `Bearer ${user.token}` },
    })
      .then((r) => r.json())
      .then((data) => {
        if (data.user_id) {
          // Check 7-day upgrade trigger
          const firstSeen = localStorage.getItem("yousift_first_seen");
          if (!firstSeen) {
            localStorage.setItem("yousift_first_seen", new Date().toISOString());
          } else {
            const daysSince = (Date.now() - new Date(firstSeen).getTime()) / (1000 * 60 * 60 * 24);
            const upgradeShownThisSession = sessionStorage.getItem("yousift_upgrade_shown");
            if (daysSince >= 7 && !upgradeShownThisSession) {
              setUpgradeReason("days");
              sessionStorage.setItem("yousift_upgrade_shown", "true");
            }
          }
        } else {
          // Token invalid — log out
          handleLogout();
        }
      })
      .catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Progress bar animation
  useEffect(() => {
    if (!loading) {
      setProgress(0);
      return;
    }
    setProgress(20);
    const interval = setInterval(() => {
      setProgress((p) => {
        if (p >= 85) { clearInterval(interval); return 85; }
        return p + (85 - p) * 0.08;
      });
    }, 400);
    return () => clearInterval(interval);
  }, [loading]);

  const handleLogout = () => {
    localStorage.removeItem("yousift_token");
    localStorage.removeItem("yousift_user_id");
    localStorage.removeItem("yousift_email");
    setUser(null);
    setResult(null);
  };

  // Called when user successfully registers or logs in
  const handleAuthSuccess = (authData) => {
    setUser(authData);
    setShowAuth(false);
    if (!authData.quiz_completed) {
      setShowQuiz(true);
    } else {
      window.location.href = "/dashboard";
    }
  };

  // Called when quiz completes
  const handleQuizComplete = () => {
    setShowQuiz(false);
    window.location.href = "/dashboard";
  };

  const handleSift = async (e) => {
    e.preventDefault();
    if (!url.trim()) return;

    const trimmedUrl = url.trim();

    if (!user) {
      // Allow one free sift before requiring login
      if (!localStorage.getItem("yousift_free_sift_used")) {
        runSift(trimmedUrl, null);
        return;
      }
      // Free use exhausted — require login
      setPendingUrl(trimmedUrl);
      sessionStorage.setItem("yousift_pending_url", trimmedUrl);
      setShowAuth(true);
      return;
    }

    // Gate: must have completed quiz
    if (!localStorage.getItem("yousift_onboarding_complete")) {
      setPendingUrl(trimmedUrl);
      sessionStorage.setItem("yousift_pending_url", trimmedUrl);
      setShowQuiz(true);
      return;
    }

    runSift(trimmedUrl, user);
  };

  const runSift = async (siftUrl, authUser) => {
    setLoading(true);
    setError("");
    setResult(null);

    // Consume the free use immediately — before the request fires —
    // so a timeout or error doesn't leave the gate open for a second free attempt.
    if (!authUser) {
      localStorage.setItem("yousift_free_sift_used", "true");
    }

    try {
      const headers = { "Content-Type": "application/json" };
      if (authUser?.token) headers.Authorization = `Bearer ${authUser.token}`;

      const res = await fetch(`${API_BASE}/api/sift`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          youtube_url: siftUrl,
          ...(authUser?.user_id ? { user_id: authUser.user_id } : {}),
        }),
      });

      const data = await res.json();

      if (!res.ok) {
        if (res.status === 402) {
          setUpgradeReason("limit");
          sessionStorage.setItem("yousift_upgrade_shown", "true");
        } else {
          setError(data.detail || "Something went wrong. Please try again.");
        }
        setLoading(false);
        return;
      }

      setProgress(100);
      setTimeout(() => {
        setLoading(false);
        setResult(data);
        setTimeout(() => {
          resultsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 100);

        // Upgrade trigger after 3 sifts
        const siftCount = data.sift_count ?? 0;
        const upgradeShownThisSession = sessionStorage.getItem("yousift_upgrade_shown");
        if (siftCount >= 3 && !upgradeShownThisSession) {
          setUpgradeReason("limit");
          sessionStorage.setItem("yousift_upgrade_shown", "true");
        }
      }, 600);
    } catch {
      setError("We couldn't reach the server. Please try again.");
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0F0F0F] flex flex-col">
      {/* Top loading bar */}
      {loading && (
        <div style={{ position: "fixed", top: 0, left: 0, width: "100%", zIndex: 9999, height: "4px", background: "#1A1A1A" }}>
          <div style={{ height: "100%", width: `${progress}%`, background: "#FF0000", transition: "width 0.3s ease-out" }} />
        </div>
      )}

      {showAuth && (
        <AuthModal
          onSuccess={handleAuthSuccess}
          onDismiss={() => { setShowAuth(false); setPendingUrl(null); }}
        />
      )}

      {showQuiz && (
        <OnboardingQuiz
          userId={user?.user_id}
          onComplete={handleQuizComplete}
        />
      )}

      {upgradeReason && (
        <UpgradeModal
          reason={upgradeReason}
          onDismiss={() => setUpgradeReason(null)}
        />
      )}

      {/* Nav */}
      <nav className="flex items-center justify-between px-6 py-4 border-b border-[#1A1A1A]">
        <Wordmark size="sm" darkBg={true} />
        {user ? (
          <div className="flex items-center gap-4">
            <span className="text-gray-500 text-sm hidden sm:block">{user.email}</span>
            <button
              onClick={handleLogout}
              className="flex items-center gap-1.5 text-gray-400 hover:text-white text-sm transition-colors"
            >
              <LogOut size={14} /> Sign out
            </button>
          </div>
        ) : (
          <button
            onClick={() => setShowAuth(true)}
            className="text-gray-400 hover:text-white text-sm font-medium transition-colors"
          >
            Sign in
          </button>
        )}
      </nav>

      {/* Hero */}
      <section className="flex flex-col items-center justify-center text-center px-4 pt-20 pb-20">
        <div className="mb-8">
          <span className="text-xs font-semibold tracking-[0.2em] uppercase text-gray-500">
            A Bethany Labs product
          </span>
        </div>

        <div className="mb-6">
          <Wordmark size="lg" darkBg={true} />
        </div>

        <h1 className="text-4xl md:text-6xl font-black text-white tracking-tight mb-4 leading-tight">
          One video. Endless content.
        </h1>

        <p className="text-gray-400 text-lg md:text-xl font-normal max-w-xl mb-12 leading-relaxed">
          Turn your YouTube videos into LinkedIn posts, blog articles, and more — in minutes.
        </p>

        <form onSubmit={handleSift} className="w-full max-w-2xl flex flex-col gap-4">
          <div className="relative w-full">
            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
              <Youtube size={20} className="text-gray-500" />
            </div>
            <input
              type="url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="Paste your YouTube URL here..."
              disabled={loading}
              className="w-full pl-12 pr-4 py-5 bg-[#1A1A1A] border border-[#2A2A2A] rounded-xl text-white text-lg placeholder-gray-600 focus:outline-none focus:border-[#FF0000] focus:ring-1 focus:ring-[#FF0000] transition-all duration-200 disabled:opacity-50"
              required
            />
          </div>

          <button
            type="submit"
            disabled={loading || !url.trim()}
            className="w-full py-5 bg-[#FF0000] hover:bg-[#CC0000] disabled:opacity-50 disabled:cursor-not-allowed text-white font-black text-lg rounded-xl transition-all duration-200 flex items-center justify-center gap-3 tracking-wide"
          >
            {loading ? (
              <>
                <Spinner />
                Sifting your content...
              </>
            ) : (
              <>
                {user ? "Sift It" : localStorage.getItem("yousift_free_sift_used") ? "Sign In to Continue" : "Start for Free"}
                <ArrowRight size={20} />
              </>
            )}
          </button>
        </form>

        {error && (
          <div className="mt-6 w-full max-w-2xl bg-red-950 border border-red-800 text-red-200 rounded-xl px-5 py-4 text-sm font-medium">
            {error}
          </div>
        )}

        {!result && !loading && (
          <p className="mt-8 text-gray-600 text-sm">
            Turns any YouTube video into a LinkedIn post + blog article in under 90 seconds.
          </p>
        )}
      </section>

      {result && (
        <section ref={resultsRef} className="bg-[#0F0F0F]">
          <ResultCards
            linkedin_post={result.linkedin_post}
            blog_post={result.blog_post}
          />
          <div className="flex justify-center pb-16">
            <button
              onClick={() => {
                if (!user && localStorage.getItem("yousift_free_sift_used")) {
                  // Free sift used — send straight to sign-up
                  window.scrollTo({ top: 0, behavior: "smooth" });
                  setShowAuth(true);
                } else {
                  setResult(null);
                  setUrl("");
                  window.scrollTo({ top: 0, behavior: "smooth" });
                }
              }}
              className="px-6 py-3 bg-[#1A1A1A] hover:bg-[#2A2A2A] border border-[#2A2A2A] text-gray-400 hover:text-white text-sm font-semibold rounded-xl transition-all"
            >
              ← Sift another video
            </button>
          </div>
        </section>
      )}

      <footer className="mt-auto py-8 text-center border-t border-[#2A2A2A]">
        <div className="flex flex-wrap items-center justify-center gap-6 mb-4">
          <a href="/pricing" className="text-gray-400 hover:text-white text-sm font-medium transition-colors">Pricing</a>
          <a href="/privacy" className="text-gray-400 hover:text-white text-sm font-medium transition-colors">Privacy Policy</a>
          <a href="/terms" className="text-gray-400 hover:text-white text-sm font-medium transition-colors">Terms of Use</a>
          <a href="/data-compliance" className="text-gray-400 hover:text-white text-sm font-medium transition-colors">Data & Compliance</a>
        </div>
        <p className="text-gray-500 text-xs">
          &copy; {new Date().getFullYear()}{" "}
          <span className="text-gray-300 font-semibold">Bethany Labs</span> · All rights reserved
        </p>
      </footer>
    </div>
  );
}

function Spinner() {
  return (
    <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
    </svg>
  );
}
